import { useState, type FormEvent } from 'react';
import { Navigate } from 'react-router';

import { ApiError } from '../api/client';
import { MatchCard } from '../components/MatchCard';
import { Button, Card, Field, FormError } from '../components/ui';
import {
  useAdminUsers,
  useConfirmMatch,
  useCreateSeason,
  useMatches,
  useRecalculate,
  useSeasons,
  useUpdateSeason,
  useUpdateUser,
  useVoidMatch,
} from '../hooks/useMatches';
import { useAuth } from '../lib/auth-context';

function DisputeQueue({ onError }: { onError: (message: string) => void }) {
  const disputed = useMatches({ status: 'disputed' });
  const confirm = useConfirmMatch();
  const voidMatch = useVoidMatch();

  const matches = disputed.data?.matches ?? [];

  async function act(action: 'confirm' | 'void', matchId: number) {
    try {
      if (action === 'confirm') await confirm.mutateAsync(matchId);
      else await voidMatch.mutateAsync(matchId);
    } catch (caught) {
      onError(caught instanceof ApiError ? caught.message : 'That action failed.');
    }
  }

  if (matches.length === 0) {
    return (
      <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">No disputed results.</p>
    );
  }

  return (
    <div className="mt-3 space-y-3">
      {matches.map((match) => (
        <MatchCard key={match.id} match={match}>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              className="w-auto"
              onClick={() => void act('confirm', match.id)}
            >
              Accept as recorded
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="w-auto"
              onClick={() => void act('void', match.id)}
            >
              Void
            </Button>
          </div>
        </MatchCard>
      ))}
    </div>
  );
}

export function AdminPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const users = useAdminUsers(isAdmin);
  const seasons = useSeasons();
  const updateUser = useUpdateUser();
  const updateSeason = useUpdateSeason();
  const createSeason = useCreateSeason();
  const recalculate = useRecalculate();

  const [error, setError] = useState<string | null>(null);
  const [seasonName, setSeasonName] = useState('');
  const [startsOn, setStartsOn] = useState(new Date().toISOString().slice(0, 10));

  // The API authorises every admin route independently; this only keeps
  // non-admins from seeing a page full of controls that would all fail.
  if (user && !isAdmin) {
    return <Navigate to="/" replace />;
  }

  async function run<T>(action: () => Promise<T>) {
    setError(null);
    try {
      await action();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That action failed.');
    }
  }

  async function handleCreateSeason(event: FormEvent) {
    event.preventDefault();
    if (seasonName.trim().length < 2) {
      setError('Give the season a name.');
      return;
    }
    await run(async () => {
      await createSeason.mutateAsync({ name: seasonName.trim(), startsOn });
      setSeasonName('');
    });
  }

  return (
    <>
      <h1 className="mb-4 text-2xl font-bold tracking-tight">Admin</h1>

      <FormError message={error} />

      <Card className="mb-4">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
          Disputed results
        </h2>
        <DisputeQueue onError={setError} />
      </Card>

      <Card className="mb-4">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
          Seasons
        </h2>

        <ul className="mt-3 space-y-2 text-sm">
          {(seasons.data ?? []).map((season) => (
            <li key={season.id} className="flex items-center justify-between gap-3">
              <span>
                {season.name}
                {season.isActive && (
                  <span className="ml-2 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">
                    active
                  </span>
                )}
              </span>
              <button
                type="button"
                onClick={() =>
                  void run(() =>
                    updateSeason.mutateAsync({ seasonId: season.id, isActive: !season.isActive }),
                  )
                }
                className="shrink-0 text-xs font-medium text-emerald-700 underline dark:text-emerald-400"
              >
                {season.isActive ? 'Close' : 'Make active'}
              </button>
            </li>
          ))}
        </ul>

        <form onSubmit={handleCreateSeason} className="mt-4 space-y-3 border-t border-slate-200 pt-4 dark:border-slate-800">
          <Field
            label="New season name"
            value={seasonName}
            onChange={(e) => setSeasonName(e.target.value)}
            maxLength={80}
          />
          <Field
            label="Starts on"
            type="date"
            value={startsOn}
            onChange={(e) => setStartsOn(e.target.value)}
          />
          <Button type="submit" disabled={createSeason.isPending}>
            Create season
          </Button>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Closing a season awards Most Improved to its biggest rating gainer.
          </p>
        </form>
      </Card>

      <Card className="mb-4">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
          Players
        </h2>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-slate-500 dark:text-slate-400">
              <tr className="border-b border-slate-200 dark:border-slate-800">
                <th scope="col" className="py-2 text-left font-medium">Player</th>
                <th scope="col" className="py-2 text-left font-medium">Role</th>
                <th scope="col" className="py-2 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(users.data ?? []).map((row) => (
                <tr key={row.id} className="border-b border-slate-100 last:border-0 dark:border-slate-800">
                  <td className="py-2">
                    {row.displayName}
                    {row.status === 'inactive' && (
                      <span className="ml-2 text-xs text-slate-400">(inactive)</span>
                    )}
                  </td>
                  <td className="py-2 text-slate-500 dark:text-slate-400">{row.role}</td>
                  <td className="py-2 text-right">
                    <button
                      type="button"
                      onClick={() =>
                        void run(() =>
                          updateUser.mutateAsync({
                            userId: row.id,
                            role: row.role === 'admin' ? 'player' : 'admin',
                          }),
                        )
                      }
                      className="text-xs font-medium text-emerald-700 underline dark:text-emerald-400"
                    >
                      {row.role === 'admin' ? 'Demote' : 'Promote'}
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        void run(() =>
                          updateUser.mutateAsync({
                            userId: row.id,
                            status: row.status === 'active' ? 'inactive' : 'active',
                          }),
                        )
                      }
                      className="ml-3 text-xs font-medium text-slate-500 underline dark:text-slate-400"
                    >
                      {row.status === 'active' ? 'Deactivate' : 'Reactivate'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
          Recalculate ratings
        </h2>
        <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
          Resets every rating and replays the whole confirmed history in order. Run this after
          voiding or correcting a match — ratings do not fix themselves, because every later match
          was rated against the old numbers.
        </p>

        <div className="mt-4">
          <Button
            type="button"
            onClick={() => void run(() => recalculate.mutateAsync())}
            disabled={recalculate.isPending}
          >
            {recalculate.isPending ? 'Replaying…' : 'Recalculate now'}
          </Button>
        </div>

        {recalculate.isSuccess && recalculate.data && (
          <p role="status" className="mt-3 text-sm text-emerald-700 dark:text-emerald-400">
            Replayed {recalculate.data.matchesReplayed} match
            {recalculate.data.matchesReplayed === 1 ? '' : 'es'} in {recalculate.data.durationMs}ms.
          </p>
        )}
      </Card>
    </>
  );
}
